import Siema from 'siema';
import fastdom from 'fastdom';
import interactiveChildren from 'aria-components/src/lib/interactiveChildren';
import breakpoints from 'utils/breakpoints';

/**
 * Carousel component.
 */
class AICarousel extends HTMLElement {
  /**
   * Create the custom element.
   */
  constructor() {
    super();

    // Bind class methods.
    this.handleMedia = this.handleMedia.bind(this);
    this.carouselSetup = this.carouselSetup.bind(this);
    this.getAriaLiveRegion = this.getAriaLiveRegion.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onSlideChange = this.onSlideChange.bind(this);
    this.updateSlideAttributes = this.updateSlideAttributes.bind(this);
    this.updateCarouselButtons = this.updateCarouselButtons.bind(this);
    this.updateAriaLiveRegion = this.updateAriaLiveRegion.bind(this);
    this.cleanup = this.cleanup.bind(this);
    this.destroy = this.destroy.bind(this);

    /**
     * The classname added when the carousel is initialized.
     *
     * @type {String}
     */
    this.initializedClass = 'ai-carousel--initialized';
  }

  /**
   * Called by the browser when the element is added to the page.
   */
  connectedCallback() {
    /**
     * The total number of slides in the carousel.
     *
     * @type {Number}
     */
    this.slideCount = this.children.length;

    // Bail early if there are no slides.
    if (0 === this.slideCount) {
      this.parentNode.removeChild(this);
      return;
    }

    /**
     * The media query based upon the `media` attribute.
     *
     * @type {MediaQueryList}
     */
    const media = this.getAttribute('media') || 'all';
    this.media = window.matchMedia(media);

    // Init.
    this.handleMedia();
    this.media.addListener(this.handleMedia);
  }

  /**
   * Navigate to the previous carousel page.
   */
  onPrevClick() {
    this.carousel.prev(this.carousel.perPage);
  }

  /**
   * Navigate to the next carousel page.
   */
  onNextClick() {
    this.carousel.next(this.carousel.perPage);
  }

  /**
   * Callback for after changing slides.
   */
  onSlideChange() {
    const { currentSlide } = this.carousel;

    this.updateSlideAttributes();
    this.updateCarouselButtons();
    this.updateAriaLiveRegion(currentSlide);

    this.previousSlide = currentSlide;
  }

  /**
   * Conditionally initialize or destroy the carousel based on the matching breakpoint.
   */
  handleMedia() {
    if (this.media.matches) {
      this.carouselSetup();
    } else if (this.carousel instanceof Siema) {
      this.destroy();
    }
  }

  /**
   * Initialize the carousel and controls.
   */
  carouselSetup() {
    // Get the perPage option value.
    const perPage = JSON.parse(this.getAttribute('per-page') || 1);

    // If `perPage` is an object, listen for breakpoint changes.
    const bpValues = Object.keys(perPage);
    if (0 < bpValues.length) {
      bpValues.forEach((value) => {
        const media = window.matchMedia(`(min-width: ${value}px)`);
        media.addEventListener('change', this.updateCarouselButtons);
      });
    }

    // Initialize the carousel.
    this.carousel = new Siema({
      selector: this,
      duration: 300,
      draggable: ! breakpoints.lgMin.matches,
      perPage,
      onChange: this.onSlideChange,
    });

    // Call the Siema resizeHandler method to ensure slides are correctly spaced.
    window.addEventListener('load', () => {
      fastdom.mutate(() => {
        this.carousel.resizeHandler();
      });
    }, { once: true });

    // Signal for styling purposes.
    fastdom.mutate(() => {
      this.classList.add(this.initializedClass);
    });

    /**
     * A reference to what will eventually be the previous slide.
     *
     * @type {Number}
     */
    this.previousSlide = this.carousel.currentSlide;

    /**
     * The previous slide control.
     *
     * @type {HTMLButtonElement}
     */
    this.prev = document.getElementById(this.getAttribute('control-previous'));

    if (null != this.prev) {
      fastdom.mutate(() => {
        this.prev.classList.add('control-previous--initialized');
      });

      this.prev.addEventListener('click', this.onPrevClick);
    }

    /**
     * The next slide control.
     *
     * @type {HTMLButtonElement}
     */
    this.next = document.getElementById(this.getAttribute('control-next'));

    if (null != this.next) {
      fastdom.mutate(() => {
        this.next.classList.add('control-next--initialized');
      });

      this.next.addEventListener('click', this.onNextClick);
    }

    // Sets button state.
    this.updateCarouselButtons();

    /**
     * The aria-live element.
     *
     * @type {HTMLDivElement}
     */
    this.ariaLiveRegion = this.getAriaLiveRegion();

    // Additional carousel attributes.
    this.setAttribute('role', 'region');
    this.tabIndex = 0;

    // Set initial states.
    this.updateSlideAttributes();
    this.updateAriaLiveRegion(0);
  }

  /**
   * Hide and prevent tab focus from leaking into overflow carousel items.
   */
  updateSlideAttributes() {
    const others = Array.from(this.carousel.innerElements);

    // Get the page.
    const current = others.splice(
      this.carousel.currentSlide,
      this.carousel.perPage,
    );

    // Disable tabbing to inactive slide content.
    others.forEach((slide) => {
      slide.setAttribute('tabindex', '-1');
      slide.setAttribute('aria-hidden', true);
      const interactiveChildElements = interactiveChildren(slide);

      // Disable the interactive children of hidden slides.
      interactiveChildElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    });

    // Allow tabbing to active slide content.
    current.forEach((slide) => {
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-hidden');

      const interactiveChildElements = interactiveChildren(slide);

      // Remove the tabindex from children of visible slides.
      interactiveChildElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    });
  }

  /**
   * Set the prev/next button disabled if at beginning/end of carousel.
   */
  updateCarouselButtons() {
    const { currentSlide } = this.carousel;

    // Previous is disabled if the carousel can't be moved back.
    const disablePrevious = (0 === currentSlide);

    // Next is disabled when the carousel can't be advanced.
    const disableNext = (
      currentSlide + this.carousel.perPage >= this.slideCount // Carousel has no page after the current page.
      || this.carousel.perPage >= this.slideCount // Carousel is has no second page.
    );

    if (null != this.prev) {
      this.prev.setAttribute('aria-disabled', `${disablePrevious}`);
    }

    if (null != this.next) {
      this.next.setAttribute('aria-disabled', `${disableNext}`);
    }
  }

  /**
   * Create and return the aria-live region.
   *
   * @return {HTMLElement} The aria-live region.
   */
  getAriaLiveRegion() {
    const div = document.createElement('div');
    div.setAttribute('aria-live', 'polite');
    div.setAttribute('aria-atomic', 'true');
    div.classList.add('screen-reader-text');

    this.parentNode.insertBefore(div, this.nextSibling);

    return div;
  }

  /**
   * Update ARIA live region to display the currently-active slide.
   *
   * @param {Number} index The current carousel slide index.
   */
  updateAriaLiveRegion(currentSlide) {
    const start = currentSlide + 1;
    const end = this.slideCount > (currentSlide + this.carousel.perPage)
      ? currentSlide + this.carousel.perPage
      : this.slideCount;

    const ariaLiveText = (1 < this.carousel.perPage)
      ? `Items ${start} through ${end} of ${this.slideCount}`
      : `Item ${start} of ${this.slideCount}`;

    this.ariaLiveRegion.textContent = ariaLiveText;
  }

  /**
   * Remove event listeners and elements created by this component.
   */
  cleanup() {
    this.ariaLiveRegion.remove();

    if (null != this.prev) {
      this.prev.removeEventListener('click', this.onPrevClick);
      this.prev.classList.remove('control-previous--initialized');
    }

    if (null != this.next) {
      this.next.removeEventListener('click', this.onNextClick);
      this.next.classList.remove('control-next--initialized');
    }
  }

  /**
   * Called by the browser when the element is removed from the page.
   */
  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Destroy the carousel and cleanup.
   */
  destroy() {
    // Destroy the carousel.
    this.carousel.destroy(
      true,
      () => {
        fastdom.mutate(() => {
          this.classList.remove(this.initializedClass);
        });
      },
    );

    // Allow tabbing to active slide content.
    Array.from(this.carousel.innerElements).forEach((slide) => {
      slide.removeAttribute('tabindex');
      slide.removeAttribute('aria-hidden');

      const interactiveChildElements = interactiveChildren(slide);

      // Remove the tabindex from children of slides.
      interactiveChildElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    });

    this.removeAttribute('tabindex');
    this.cleanup();
  }
}

// Define the custom elements.
customElements.define('ai-carousel', AICarousel);
